export const ROUTES = {
	/** The console's index: the first-run guide, and the status board after it. */
	getStarted: '/',
	signatures: '/signatures',
	/** The designer's old address, kept as a redirect to Signatures for bookmarks. */
	designer: '/designer',
	templates: '/templates',
	rules: '/rules',
	identities: '/coverage',
	connections: '/connections',
	account: '/account',
	audit: '/audit',
	team: '/team',

	/**
	 * Settings — the highest-privilege area, and **one route per sidebar item**.
	 *
	 * A route each rather than tabs or one long sectioned page: an administrator arrives
	 * knowing what they came to change, and one destination per item means the sidebar, the
	 * page's own heading and the browser's back button all agree about where they are. It
	 * also lets a link point at Billing without dragging the other four areas along.
	 *
	 * `/settings` itself is not a screen — it redirects to Organisation, so the section has a
	 * landing place without an index that exists only to be clicked through.
	 */
	settings: '/settings',
	settingsAccount: '/settings/account',
	settingsOrganisation: '/settings/organisation',
	settingsBilling: '/settings/billing',
	settingsUsers: '/settings/users',
	settingsRoles: '/settings/roles',
	settingsAudit: '/settings/audit',

	/**
	 * Where an invitation email points. Its own route rather than the console's `/team`:
	 * the person following the link is usually not a member yet, so the screen has to answer
	 * for somebody who cannot see a roster — and it has to offer a *decision*, accept or
	 * decline, rather than dropping them into the organisation on arrival.
	 */
	invitation: '/invitations/:id',

	signIn: '/sign-in',

	signInVerify: '/sign-in/verify',
	signUp: '/sign-up',

	signUpVerify: '/sign-up/verify',

	join: '/join',

	chooseOrganisation: '/choose-organisation',

	newOrganisation: '/organisations/new',

	onboardingName: '/onboarding/name',
	onboardingOrganisation: '/onboarding/organisation',
} as const

export type RouteName = keyof typeof ROUTES

export const ROUTE_FOR: Record<string, RouteName | undefined> = {
	[ROUTES.getStarted]: 'getStarted',
	[ROUTES.signatures]: 'signatures',
	[ROUTES.designer]: 'designer',
	[ROUTES.templates]: 'templates',
	[ROUTES.rules]: 'rules',
	[ROUTES.identities]: 'identities',
	[ROUTES.connections]: 'connections',
	[ROUTES.account]: 'account',
	[ROUTES.audit]: 'audit',
	[ROUTES.team]: 'team',
	[ROUTES.settings]: 'settings',
	[ROUTES.settingsAccount]: 'settingsAccount',
	[ROUTES.settingsOrganisation]: 'settingsOrganisation',
	[ROUTES.settingsBilling]: 'settingsBilling',
	[ROUTES.settingsUsers]: 'settingsUsers',
	[ROUTES.settingsRoles]: 'settingsRoles',
	[ROUTES.settingsAudit]: 'settingsAudit',
	[ROUTES.signIn]: 'signIn',
	[ROUTES.signInVerify]: 'signInVerify',
	[ROUTES.signUp]: 'signUp',
	[ROUTES.signUpVerify]: 'signUpVerify',
	[ROUTES.join]: 'join',
	[ROUTES.chooseOrganisation]: 'chooseOrganisation',
	[ROUTES.newOrganisation]: 'newOrganisation',
	[ROUTES.onboardingName]: 'onboardingName',
	[ROUTES.onboardingOrganisation]: 'onboardingOrganisation',
}

export function isOnboarding(pathname: string): boolean {
	return pathname.startsWith('/onboarding')
}

export function isConsolePath(pathname: string): boolean {
	const name = routeNameFor(pathname)
	return name !== null && CONSOLE_PATH_NAMES.has(name)
}

const CONSOLE_PATH_NAMES = new Set<RouteName>([
	'getStarted',
	'signatures',
	'designer',
	'templates',
	'rules',
	'identities',
	'connections',
	'account',
	'audit',
	'team',
	'settings',
	'settingsAccount',
	'settingsOrganisation',
	'settingsBilling',
	'settingsUsers',
	'settingsRoles',
	'settingsAudit',
	'join',
	'invitation',
])

/**
 * The invitation deep link, as a real path.
 *
 * `ROUTES.invitation` is a route *pattern* (`/invitations/:id`), so it cannot be handed to
 * `navigate` as it stands. Everything that builds a link to it goes through here, so the
 * pattern and the path cannot drift.
 */
export function invitationPath(invitationId: string): string {
	return `/invitations/${encodeURIComponent(invitationId)}`
}

/**
 * The pages *inside* a settings destination: forms that need the room of a page rather than
 * a dialog — a role's sixteen permissions, a pasted list of addresses — and that a person
 * should be able to link to, reload and leave with the back button.
 *
 * Kept out of `ROUTES` on purpose. They are not sidebar destinations, so `routeNameFor`
 * answers with the destination that owns them: the sidebar keeps Roles (or Users) lit while
 * the person is creating a role, and the bar's title stays the area they are working in.
 */
export const SETTINGS_PAGES = {
	roleNew: '/settings/roles/new',
	rolesCompare: '/settings/roles/compare',
	roleEdit: '/settings/roles/:key/edit',
	usersInvite: '/settings/users/invite',
	billingInvoices: '/settings/billing/invoices',
} as const

/** The designer is a page inside Signatures: `new`, or the id of the signature being edited. */
export const SIGNATURE_PAGES = {
	edit: '/signatures/:id',
} as const

export const NEW_SIGNATURE = 'new'

export function signaturePath(id: string = NEW_SIGNATURE): string {
	return `${ROUTES.signatures}/${encodeURIComponent(id)}`
}

/** Creating and editing a rule are pages inside Rules, so the sidebar keeps Rules lit. */
export const RULE_PAGES = {
	new: '/rules/new',
	edit: '/rules/:id',
} as const

export function ruleEditPath(id: string): string {
	return `${ROUTES.rules}/${encodeURIComponent(id)}`
}

/** Connecting a provider is a page inside Connections, so the sidebar keeps Connections lit. */
export const CONNECTION_PAGES = {
	connect: '/connections/connect',
	directory: '/connections/:id',
} as const

/** Everyone one connection imported. */
export function connectionPath(id: string): string {
	return `${ROUTES.connections}/${encodeURIComponent(id)}`
}

export function connectPath(provider?: string): string {
	return provider ? `${CONNECTION_PAGES.connect}?provider=${encodeURIComponent(provider)}` : CONNECTION_PAGES.connect
}

/** A new role, optionally starting from an existing role's permissions. */
export function roleNewPath(from?: string): string {
	return from ? `${SETTINGS_PAGES.roleNew}?from=${encodeURIComponent(from)}` : SETTINGS_PAGES.roleNew
}

export function roleEditPath(key: string): string {
	return `/settings/roles/${encodeURIComponent(key)}/edit`
}

export function routeNameFor(pathname: string): RouteName | null {
	const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
	const exact = ROUTE_FOR[path === '' ? ROUTES.getStarted : path]
	if (exact) return exact

	/* The one parameterised route. Matched by prefix rather than by a second table: a
	 * `/invitations/<anything>` is the invitation screen, and the id is the screen's to
	 * validate — an id that does not exist gets the same "not found" as a typo. */
	if (path.startsWith('/invitations/')) return 'invitation'
	if (path.startsWith(`${ROUTES.connections}/`)) return 'connections'
	if (path.startsWith(`${ROUTES.signatures}/`)) return 'signatures'
	if (path.startsWith(`${ROUTES.rules}/`)) return 'rules'
	if (path.startsWith(`${ROUTES.settingsRoles}/`)) return 'settingsRoles'
	if (path.startsWith(`${ROUTES.settingsUsers}/`)) return 'settingsUsers'
	if (path.startsWith(`${ROUTES.settingsBilling}/`)) return 'settingsBilling'
	return null
}

export function readSignInError(search: string): string | null {
	const params = new URLSearchParams(search)
	const error = params.get('error')
	if (!error) return null

	const detail = params.get('error_detail')
	return detail ? `${error}: ${detail}` : error
}

export function describeSignInError(code: string): string {
	const [head] = code.split(':')
	switch (head) {
		case 'provider_error':
			return 'Your identity provider refused the sign-in.'
		case 'not_configured':
			return 'Sign-in is not configured on this deployment yet.'

		case 'INVALID_OTP':
			return 'That code is not right. Check the most recent email and try again.'
		case 'OTP_EXPIRED':
			return 'That code has expired. Send a new one.'
		case 'TOO_MANY_ATTEMPTS':
			return 'Too many attempts with that code. Send a new one.'
		case 'INVALID_EMAIL':
			return 'That does not look like an email address.'
		case 'PASSKEY_NOT_FOUND':
			return 'No passkey is registered for this account on this device.'
		case 'AUTH_CANCELLED':
			return 'That passkey prompt was dismissed.'
		case 'MISSING_OR_NULL_ORIGIN':
			return 'The browser did not send an origin, so this sign-in was refused. Reload and try again.'
		case 'INVALID_ORIGIN':
			return 'This address is not one the deployment trusts for sign-in.'
		case 'PROVIDER_NOT_FOUND':
			return 'That sign-in provider is not configured on this deployment.'
		case 'INVALID_CALLBACK_URL':
			return 'That return address is not one this deployment allows.'
		default:
			return `Sign-in failed (${code}).`
	}
}

export function isSafeRedirectPath(value: string): boolean {
	return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
}
